import { useState, useEffect, useCallback, memo } from 'react';
import { Av, Bdg, PH } from '../../components/shared';
import { ModalNewSubteam } from '../../components/ModalNewSubteam';
import { useApp } from '../../context/AppContext';
import * as api from '../../services/api';

function formatDate(d) {
  if (!d) return '—';
  const date = new Date(d);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function daysBetween(start, end) {
  if (!start || !end) return 0;
  const ms = new Date(end) - new Date(start);
  return Math.max(Math.ceil(ms / 86400000), 0);
}

export const PageSubteams = memo(()=>{
  const { showToast } = useApp();
  const [subteams, setSubteams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [detail, setDetail] = useState(null);

  const fetchSubteams = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getSubteams();
      const d = res?.data ?? res;
      const items = Array.isArray(d) ? d : (Array.isArray(d?.data) ? d.data : []);
      setSubteams(items);
    } catch (e) {
      showToast(e.message || 'Failed to load subteams', 'error');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSubteams(); }, []);

  const handleSave = (newTeam) => {
    fetchSubteams();
    showToast(`Subteam "${newTeam.name}" created!`);
  };

  const handleEdit = () => {
    fetchSubteams();
    showToast('Subteam updated!');
  };

  // ── If viewing detail, show SubteamDetail ──
  if (detail) {
    const s = subteams.find(x=> x.id === detail);
    if (!s) { setDetail(null); return null; }
    return <SubteamDetail subteam={s} onBack={()=>setDetail(null)} onEdit={handleEdit} />;
  }

  const statusColor = {active:"#059669", completed:"#0891b2", expired:"#dc2626"};
  const statusBg    = {active:"rgba(5,150,105,.09)", completed:"rgba(8,145,178,.09)", expired:"rgba(220,38,38,.09)"};

  return (
    <div className="pg fi">
      {showModal && <ModalNewSubteam onClose={()=>setShowModal(false)} onSave={handleSave}/>}
      <PH eyebrow="Team" title="Subteams" sub={`${subteams.length} subteams`}
        action={<button className="btn bp" onClick={()=>setShowModal(true)}>+ New Subteam</button>}/>

      {loading ? (
        <div style={{padding:40,textAlign:"center",color:"var(--t3)",fontSize:13}}>Loading subteams...</div>
      ) : subteams.length === 0 ? (
        <div style={{padding:40,textAlign:"center",color:"var(--t3)",fontSize:13}}>
          No subteams yet. Create your first one!
        </div>
      ) : (
      <div className="g4">
        {subteams.map((s,i)=>{
          const targets = {
            taps: s.target_taps || 0,
            connects: s.target_connects || 0,
            saves: s.target_saves || 0,
            views: s.target_views || 0,
          };
          const achieved = {
            taps: s.achieved_taps || 0,
            connects: s.achieved_connects || 0,
            saves: s.achieved_saves || 0,
            views: s.achieved_views || 0,
          };
          const hasTargets = targets.taps + targets.connects + targets.saves + targets.views > 0;
          const overallPct = hasTargets
            ? Math.round(
                (['taps','connects','saves','views'].reduce((sum,k) => sum + (targets[k] > 0 ? achieved[k]/targets[k] : 0), 0)
                / ['taps','connects','saves','views'].filter(k => targets[k] > 0).length) * 100
              )
            : 0;
          const sc = statusColor[s.status]||"#64748b";
          const sb = statusBg[s.status]||"rgba(100,116,139,.09)";
          const color = s.color || "#0891b2";
          const icon = s.icon || "🏆";
          const days = daysBetween(s.period_start, s.period_end);

          return (
            <div key={s.id} className={`card fi s${(i%4)+1}`} style={{padding:22,cursor:"pointer",position:"relative",overflow:"hidden"}}
              onClick={()=>setDetail(s.id)}
              onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-3px)";e.currentTarget.style.boxShadow=`0 12px 40px rgba(0,0,0,.12)`;}}
              onMouseLeave={e=>{e.currentTarget.style.transform="";e.currentTarget.style.boxShadow="";}}>

              {/* Top accent line */}
              <div style={{position:"absolute",top:0,left:0,right:0,height:3,background:`linear-gradient(90deg,${color},${color}66)`}}/>

              {/* Header */}
              <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:14}}>
                <div style={{width:44,height:44,borderRadius:13,background:`${color}12`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,boxShadow:`0 4px 14px ${color}22`}}>{icon}</div>
                <span style={{fontSize:10.5,fontWeight:650,padding:"3px 9px",borderRadius:20,background:sb,color:sc,border:`1px solid ${sc}22`,textTransform:"capitalize"}}>{s.status || 'active'}</span>
              </div>

              <div style={{fontSize:15,fontWeight:680,marginBottom:3,letterSpacing:"-.3px",color:"var(--t1)"}}>{s.name}</div>
              <div style={{fontSize:11.5,color:"var(--t3)",marginBottom:12}}>
                {s.lead && <>Lead: <span style={{color:"var(--t2)",fontWeight:560}}>{s.lead}</span> · </>}
                {days > 0 ? `${days} days` : 'No period set'}
              </div>

              {/* Overall progress ring + pct */}
              <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14}}>
                <div style={{position:"relative",width:46,height:46,flexShrink:0}}>
                  <svg width="46" height="46" viewBox="0 0 46 46">
                    <circle cx="23" cy="23" r="18" fill="none" stroke="rgba(0,0,0,.07)" strokeWidth="5"/>
                    <circle cx="23" cy="23" r="18" fill="none" stroke={s.status==="completed"?"#0891b2":color} strokeWidth="5"
                      strokeDasharray={`${Math.min(overallPct,100)/100*113.1} 113.1`}
                      strokeDashoffset="28.3" strokeLinecap="round"/>
                  </svg>
                  <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:780,color:s.status==="completed"?"#0891b2":color}}>{Math.min(overallPct,100)}%</div>
                </div>
                <div>
                  <div style={{fontSize:12,fontWeight:640,color:"var(--t1)"}}>Overall Progress</div>
                  <div style={{fontSize:10.5,color:"var(--t3)",marginTop:1}}>
                    {s.period_start && s.period_end
                      ? `${formatDate(s.period_start)} → ${formatDate(s.period_end)}`
                      : 'No dates set'}
                  </div>
                </div>
              </div>

              {/* 4 metric mini-bars */}
              {hasTargets && (
              <div style={{display:"flex",flexDirection:"column",gap:5,marginBottom:14}}>
                {[
                  {k:"taps",    lbl:"Taps",     col:"#0891b2"},
                  {k:"connects",lbl:"Connects", col:"#059669"},
                  {k:"saves",   lbl:"Saves",    col:"#7c3aed"},
                  {k:"views",   lbl:"Views",    col:"#d97706"},
                ].map(({k,lbl,col})=>{
                  const pct = targets[k]>0 ? Math.min(Math.round(achieved[k]/targets[k]*100),100) : 0;
                  return (
                    <div key={k}>
                      <div style={{display:"flex",justifyContent:"space-between",fontSize:10.5,marginBottom:2}}>
                        <span style={{color:"var(--t3)"}}>{lbl}</span>
                        <span style={{color:col,fontWeight:660}}>{achieved[k].toLocaleString()} / {targets[k].toLocaleString()}</span>
                      </div>
                      <div className="pb" style={{height:4}}>
                        <div className="pb-f" style={{width:`${pct}%`,background:col,borderRadius:2}}/>
                      </div>
                    </div>
                  );
                })}
              </div>
              )}

              {/* Footer — member avatar stack */}
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div style={{display:"flex",alignItems:"center"}}>
                  {(s.member_previews || []).slice(0,4).map((m, idx) => {
                    const initials = (m.name || m.username || '??').slice(0,2).toUpperCase();
                    return m.photo ? (
                      <img key={idx} src={m.photo} alt="" style={{width:26,height:26,borderRadius:"50%",objectFit:"cover",border:"2px solid #fff",marginLeft:idx>0?-8:0,boxShadow:"0 1px 4px rgba(0,0,0,.15)",position:"relative",zIndex:5-idx}}/>
                    ) : (
                      <div key={idx} style={{width:26,height:26,borderRadius:"50%",background:"linear-gradient(150deg,#64748b,#059669)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,fontWeight:700,color:"#fff",border:"2px solid #fff",marginLeft:idx>0?-8:0,boxShadow:"0 1px 4px rgba(0,0,0,.15)",position:"relative",zIndex:5-idx,flexShrink:0}}>{initials}</div>
                    );
                  })}
                  {(s.profile_count || 0) > 4 && (
                    <div style={{width:26,height:26,borderRadius:"50%",background:"rgba(100,116,139,.15)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:8,fontWeight:600,color:"var(--t3)",border:"2px solid #fff",marginLeft:-8,position:"relative",zIndex:0,flexShrink:0}}>+{(s.profile_count||0)-4}</div>
                  )}
                  {(s.profile_count || 0) === 0 && (
                    <span style={{fontSize:11.5,color:"var(--t3)"}}>No members</span>
                  )}
                </div>
                <span style={{fontSize:11,color:"var(--blue)",fontWeight:580}}>View details →</span>
              </div>
            </div>
          );
        })}

        {/* Add card */}
        <div className="card fi" style={{padding:22,cursor:"pointer",border:"2px dashed rgba(100,116,139,.2)",background:"rgba(255,255,255,.3)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:10,minHeight:220}}
          onClick={()=>setShowModal(true)}>
          <div style={{width:44,height:44,borderRadius:13,background:"rgba(100,116,139,.1)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,color:"var(--blue)"}}>+</div>
          <div style={{fontSize:13,fontWeight:570,color:"var(--t3)"}}>Add Subteam</div>
        </div>
      </div>
      )}
    </div>
  );
});

/* ════════════ SUBTEAM DETAIL PAGE ════════════ */
export const SubteamDetail = memo(({subteam:s, onBack, onEdit})=>{
  const [memberStats, setMemberStats] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    setLoadingStats(true);
    api.getSubteamStats(s.id).then(res => {
      const data = res.data || res;
      setMemberStats(Array.isArray(data) ? data : []);
    }).catch(() => {}).finally(() => setLoadingStats(false));
  }, [s.id]);

  const color = s.color || "#0891b2";
  const sc = s.status==="completed"?"#0891b2":s.status==="expired"?"#dc2626":color;

  const targets = {
    taps: s.target_taps || 0,
    connects: s.target_connects || 0,
    saves: s.target_saves || 0,
    views: s.target_views || 0,
  };

  // Sum achieved from member stats
  const achieved = memberStats.reduce((acc, m) => ({
    taps: acc.taps + (m.taps || 0),
    connects: acc.connects + (m.connects || 0),
    saves: acc.saves + (m.saves || 0),
    views: acc.views + (m.views || 0),
  }), { taps: 0, connects: 0, saves: 0, views: 0 });

  const metrics = [
    {k:"taps",    lbl:"Taps",     ico:"💳", col:"#0891b2"},
    {k:"connects",lbl:"Connects", ico:"🤝", col:"#059669"},
    {k:"saves",   lbl:"Saves",    ico:"💾", col:"#7c3aed"},
    {k:"views",   lbl:"Views",    ico:"👁",  col:"#d97706"},
  ];

  const hasTargets = targets.taps + targets.connects + targets.saves + targets.views > 0;
  const activeMetrics = metrics.filter(({k})=>targets[k]>0);
  const overallPct = activeMetrics.length > 0
    ? Math.round(activeMetrics.reduce((sum,{k})=> sum + achieved[k]/targets[k], 0) / activeMetrics.length * 100)
    : 0;

  const days = s.period_start && s.period_end
    ? Math.max(Math.ceil((new Date(s.period_end) - new Date(s.period_start)) / 86400000), 0)
    : 0;

  // Build member rows with contribution %
  const totalTarget = metrics.reduce((sum,{k})=>sum+targets[k],0);
  const memberRows = memberStats.map(m => {
    const totalContrib = (m.taps||0)+(m.connects||0)+(m.saves||0)+(m.views||0);
    const contribPct = totalTarget>0 ? Math.round(totalContrib/totalTarget*100) : 0;
    return { ...m, contribPct };
  });

  return (
    <div className="pg fi">
      {showEditModal && <ModalNewSubteam subteam={s} onClose={()=>setShowEditModal(false)} onSave={()=>{setShowEditModal(false); onEdit?.();}}/>}

      {/* Back + header */}
      <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:24}}>
        <button className="btn bs" style={{padding:"7px 14px",fontSize:12,gap:6}} onClick={onBack}>
          ← Back
        </button>
        <div style={{width:48,height:48,borderRadius:14,background:`${color}14`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,boxShadow:`0 4px 16px ${color}22`}}>{s.icon || "🏆"}</div>
        <div>
          <div style={{fontSize:22,fontWeight:720,letterSpacing:"-.4px",color:"var(--t1)"}}>{s.name}</div>
          <div style={{fontSize:12.5,color:"var(--t3)",marginTop:2}}>
            {s.description || 'No description'} · <span style={{color:sc,fontWeight:600,textTransform:"capitalize"}}>{s.status || 'active'}</span>
          </div>
        </div>
        <button className="btn bp" style={{marginLeft:"auto",padding:"8px 16px",fontSize:12,gap:6}} onClick={()=>setShowEditModal(true)}>
          Edit Subteam
        </button>
        <div style={{textAlign:"right"}}>
          <div style={{fontSize:11,color:"var(--t3)"}}>Period</div>
          <div style={{fontSize:13,fontWeight:640,color:"var(--t1)"}}>
            {s.period_start && s.period_end
              ? `${formatDate(s.period_start)} → ${formatDate(s.period_end)}`
              : 'No dates set'}
          </div>
          {days > 0 && <div style={{fontSize:11,color:"var(--t3)",marginTop:1}}>{days} days</div>}
        </div>
      </div>

      {/* ── KPI summary strip ── */}
      {hasTargets && (
      <div className="g4" style={{marginBottom:18}}>
        {metrics.map(({k,lbl,ico,col},i)=>{
          const pct = targets[k]>0 ? Math.min(Math.round(achieved[k]/targets[k]*100),100) : 0;
          return (
            <div key={k} className={`card sc fi s${i+1}`} style={{"--sc":col,padding:20}}>
              <div className="sc-orb"/>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
                <div className="sc-ico" style={{background:`${col}12`,margin:0,width:34,height:34}}><span style={{fontSize:16}}>{ico}</span></div>
                <span style={{fontSize:12,fontWeight:700,color:col}}>{pct}%</span>
              </div>
              <div className="sc-l">{lbl}</div>
              <div style={{fontSize:22,fontWeight:730,color:"var(--t1)",letterSpacing:"-1px",margin:"4px 0"}}>{achieved[k].toLocaleString()}</div>
              <div style={{fontSize:11,color:"var(--t3)"}}>of {targets[k].toLocaleString()} target</div>
              <div className="pb" style={{marginTop:8,height:4}}>
                <div className="pb-f" style={{width:`${pct}%`,background:col,borderRadius:2}}/>
              </div>
            </div>
          );
        })}
      </div>
      )}

      {/* ── Overall progress ── */}
      <div className="card-s fi s1" style={{padding:"18px 22px",marginBottom:18}}>
        <div style={{display:"flex",alignItems:"center",gap:20,flexWrap:"wrap"}}>
          <div style={{position:"relative",width:80,height:80,flexShrink:0}}>
            <svg width="80" height="80" viewBox="0 0 80 80">
              <circle cx="40" cy="40" r="32" fill="none" stroke="rgba(0,0,0,.07)" strokeWidth="8"/>
              <circle cx="40" cy="40" r="32" fill="none" stroke={sc} strokeWidth="8"
                strokeDasharray={`${Math.min(overallPct,100)/100*201.1} 201.1`}
                strokeDashoffset="50.3" strokeLinecap="round"
                style={{transition:"stroke-dasharray 1.2s cubic-bezier(.16,1,.3,1)"}}/>
            </svg>
            <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center"}}>
              <div style={{fontSize:18,fontWeight:800,color:sc,letterSpacing:"-1px"}}>{Math.min(overallPct,100)}%</div>
            </div>
          </div>
          <div style={{flex:1,minWidth:180}}>
            <div style={{fontSize:16,fontWeight:680,color:"var(--t1)",marginBottom:4}}>Overall Completion</div>
            <div style={{fontSize:12.5,color:"var(--t3)",marginBottom:10}}>
              {s.period_start && s.period_end
                ? `${formatDate(s.period_start)} → ${formatDate(s.period_end)}`
                : 'No dates set'} · {memberStats.length} contributors
            </div>
            <div className="pb" style={{height:8,borderRadius:4}}>
              <div className="pb-f" style={{width:`${Math.min(overallPct,100)}%`,background:`linear-gradient(90deg,${sc},${sc}88)`,borderRadius:4}}/>
            </div>
          </div>
          <div style={{flexShrink:0,padding:"10px 18px",background:`${sc}0d`,border:`1px solid ${sc}22`,borderRadius:12,textAlign:"center"}}>
            <div style={{fontSize:22,marginBottom:4}}>{s.status==="completed"?"🏆":s.status==="expired"?"⏰":"⚡"}</div>
            <div style={{fontSize:12,fontWeight:660,color:sc,textTransform:"capitalize"}}>{s.status || 'active'}</div>
            {days > 0 && <div style={{fontSize:10.5,color:"var(--t3)",marginTop:1}}>{days} days</div>}
          </div>
          {s.territory&&<div style={{flexShrink:0,padding:"10px 18px",background:"rgba(100,116,139,.06)",border:"1px solid rgba(100,116,139,.12)",borderRadius:12,textAlign:"center"}}>
            <div style={{fontSize:20,marginBottom:4}}>📍</div>
            <div style={{fontSize:11,fontWeight:620,color:"var(--t2)"}}>{s.territory}</div>
          </div>}
        </div>
      </div>

      {/* ── Member contribution table ── */}
      <div className="card-s fi s2">
        <div className="ch">
          <div><div className="ct">Member Contributions</div><div className="cs">{memberStats.length} members · performance within {s.period_start && s.period_end ? `${formatDate(s.period_start)} → ${formatDate(s.period_end)}` : 'all time'}</div></div>
        </div>
        {loadingStats ? (
          <div style={{padding:30,textAlign:"center",color:"var(--t3)",fontSize:13}}>Loading stats...</div>
        ) : memberRows.length === 0 ? (
          <div style={{padding:30,textAlign:"center",color:"var(--t3)",fontSize:13}}>No members in this subteam</div>
        ) : (
        <>
        <table className="tbl">
          <thead>
            <tr>
              <th>Member</th>
              <th>💳 Taps</th>
              <th>🤝 Connects</th>
              <th>💾 Saves</th>
              <th>👁 Views</th>
              <th>Contribution</th>
            </tr>
          </thead>
          <tbody>
            {memberRows.map(m=>(
              <tr key={m.id}>
                <td>
                  <div style={{display:"flex",alignItems:"center",gap:9}}>
                    {m.photo ? (
                      <img src={m.photo} alt="" style={{width:30,height:30,borderRadius:"50%",objectFit:"cover",flexShrink:0}}/>
                    ) : (
                      <Av name={(m.name || m.username || "??").slice(0,2).toUpperCase()} grad="linear-gradient(150deg,#64748b,#059669)" size={30}/>
                    )}
                    <div>
                      <div style={{fontWeight:580,fontSize:12.5}}>{m.name || m.username || '—'}</div>
                      <div style={{fontSize:10.5,color:"var(--t3)"}}>{m.email || '—'}</div>
                    </div>
                  </div>
                </td>
                <td><b style={{color:"#0891b2"}}>{(m.taps||0).toLocaleString()}</b></td>
                <td><b style={{color:"#059669"}}>{(m.connects||0).toLocaleString()}</b></td>
                <td><b style={{color:"#7c3aed"}}>{(m.saves||0).toLocaleString()}</b></td>
                <td><b style={{color:"#d97706"}}>{(m.views||0).toLocaleString()}</b></td>
                <td>
                  <div style={{display:"flex",alignItems:"center",gap:8,minWidth:120}}>
                    <div style={{flex:1}}>
                      <div className="pb" style={{height:6,borderRadius:3}}>
                        <div className="pb-f" style={{width:`${Math.min(m.contribPct,100)}%`,background:color,borderRadius:3}}/>
                      </div>
                    </div>
                    <span style={{fontSize:12,fontWeight:700,color,minWidth:34,textAlign:"right"}}>{m.contribPct}%</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {/* Totals row */}
        <div style={{padding:"12px 16px",borderTop:"2px solid rgba(0,0,0,.07)",display:"flex",alignItems:"center",gap:8,background:"rgba(0,0,0,.015)"}}>
          <div style={{fontSize:12,fontWeight:660,color:"var(--t2)",flex:1,paddingLeft:39}}>Team Total</div>
          {metrics.map(({k,col})=>(
            <div key={k} style={{minWidth:70,textAlign:"left"}}>
              <b style={{fontSize:12.5,color:col}}>{achieved[k].toLocaleString()}</b>
              {targets[k] > 0 && <span style={{fontSize:10.5,color:"var(--t4)",marginLeft:4}}>/ {targets[k].toLocaleString()}</span>}
            </div>
          ))}
          <div style={{minWidth:152}}/>
        </div>
        </>
        )}
      </div>
    </div>
  );
});
